import Navbar from "../components/mainComponents/Navbar";
import Footer from "../components/mainComponents/Footer";
import LegalDocument from "../components/legal/LegalDocument";
import legalMarkdown from "../content/legal/privacy-policy.md";
import { FC, useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { GoogleGeocodeLocation, libraries } from "utils/googleMapsUtils";
import LocationModal from "@/components/location/LocationModal";
import { useRouter } from "next/router";
import { GetServerSidePropsContext } from "next/types";
import { submitAiSearch } from "../utils/submitAiSearch";
import Head from "next/head";
import Link from "next/link";
import { OPEN_CONSENT_SETTINGS_EVENT } from "../lib/consent";
import { getLegalPage } from "../lib/legalDocuments";
import { renderLegalMarkdown } from "../lib/legal/renderLegalMarkdown";

export const getServerSideProps = (context: GetServerSidePropsContext) => {
  const webView = context.query.webView;
  return {
    props: {
      webView: !!webView,
      // Rendered here so markdown-it stays out of the client bundle.
      html: renderLegalMarkdown(legalMarkdown),
    },
  };
};

const PrivacyPolicy: FC<{ webView: boolean; html: string }> = ({
  webView,
  html,
}) => {
  const [zipcode, setZipcode] = useState("");
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState("");
  const [geocodeLocation, setGeocodeLocation] =
    useState<GoogleGeocodeLocation>(null);
  const router = useRouter();
  const [searchQueryError, setSearchQueryError] = useState("");
  const [zipcodeError, setZipcodeError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const handleSubmit = (e, curZipcode = zipcode) => {
    e.preventDefault();
    submitAiSearch({
      rawQuery: searchQuery,
      router,
      setSearchQueryError,
      setZipcodeError,
      fallbackZipcode: curZipcode,
    }).catch((error) => {
      console.error("Search submission failed", error);
      setSearchQueryError("Unable to run search right now.");
    });
  };

  const handleLocationInputClick = () => {
    setOpen(true);
  };

  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: libraries,
  });

  const props = {
    navbar: {
      handleLocationInputClick,
      zipcode,
      setZipcode,
      handleSubmit,
      setSearchQuery,
      googleLoaded: isLoaded,
    },
    locationModal: {
      open,
      setOpen,
      openRef,
      zipcode,
      setZipcode,
      isLoaded,
      apiKey,
      setLocation,
      geocodeLocation,
      setGeocodeLocation,
      handleSubmit,
    },
  };
  const legalPage = getLegalPage("/privacy-policy");

  return (
    <div>
      <Head>
        <meta name="description" content={legalPage?.description ?? ""} />
      </Head>
      {!webView && <Navbar {...props.navbar} />}
      {open ? <LocationModal {...props.locationModal} /> : null}

      <section className="bg-white px-6 pb-16 md:px-24 lg:px-36 xl:px-48">
        <nav aria-label="Privacy and terms of service" className="mx-auto flex max-w-5xl flex-wrap gap-3 py-6">
          <Link href="/privacy-policy" aria-current="page" className="rounded-xl border px-4 py-3 font-semibold text-[#285B3F]">Privacy Policy</Link>
          <Link href="/terms" className="rounded-xl border px-4 py-3 font-semibold text-[#285B3F]">Terms of Service</Link>
          <Link href="/permissions" className="rounded-xl border px-4 py-3 font-semibold text-[#285B3F]">Permissions Notice</Link>
          <button type="button" className="rounded-xl border px-4 py-3 font-semibold text-[#285B3F]" onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS_EVENT))}>Privacy choices</button>
        </nav>
        <LegalDocument
          title={legalPage?.label ?? ""}
          html={html}
          sectionId="privacyPolicy"
        />
      </section>

      {!webView && <Footer />}
    </div>
  );
};

export default PrivacyPolicy;
